import { AssetColumns } from "~/modules/assets/types";
import { CatalogMetricSlugs, CatalogReferenceDataSlugs } from "../../types";
import { ColumnMetadata } from "~/components/DataGrid/types";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { WEBSITE_COLUMN } from "~/modules/assets/components/AssetTable/columns/Website";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import { PRICE_COLUMN } from "~/modules/assets/components/AssetTable/columns/Price";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { ANNUAL_RETURN_TARGET_DESCRIPTION_COLUMN } from "~/modules/assets/components/AssetTable/columns/AnnualReturnTargetDescription";
import { ASSET_CLASS_COLUMN } from "~/modules/assets/components/AssetTable/columns/AssetClass";
import { ASSET_TO_PEGGED_ASSET_RATIO_COLUMN } from "~/modules/assets/components/AssetTable/columns/AssetToPeggedAssetRatio";
import {
    AUDITOR_COLUMN,
    CRYPTO_BROKER_COLUMN,
    CRYPTO_CUSTODIAN_COLUMN,
    EXCHANGE_AGENT_COLUMN,
    FUND_ADMINISTRATOR_COLUMN,
    PAYING_AGENT_COLUMN,
    SUB_ADVISOR_COLUMN,
    TRADITIONAL_BROKER_COLUMN,
    TRANSFER_AGENT_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/ServiceProviders";
import { CUSIP_COLUMN } from "~/modules/assets/components/AssetTable/columns/Cusip";
import { DISPUTE_RESOLUTION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/DisputeResolutionCountry";
import { DISPUTE_RESOLUTION_DESCRIPTION_COLUMN } from "~/modules/assets/components/AssetTable/columns/DisputeResolutionDescription";
import { DISTRIBUTION_FREQUENCY_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/DistributionFrequencyType";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import {
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    PRIMARY_MARKET_BASE_ASSETS_COLUMN,
    PRIMARY_MARKET_IS_KYC_REQUIRED_COLUMN,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    PRIMARY_MARKET_REDEMPTION_FEE_COLUMN,
    PRIMARY_MARKET_REDEMPTION_TIME_COLUMN,
    PRIMARY_MARKET_SUBSCRIPTION_DESCRIPTION_COLUMN,
    PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    PRIMARY_MARKET_SUBSCRIPTION_TIME_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { INCEPTION_DATE_COLUMN } from "~/modules/assets/components/AssetTable/columns/InceptionDate";
import { ISIN_COLUMN } from "~/modules/assets/components/AssetTable/columns/Isin";
import { ISSUER_COLUMN } from "~/modules/assets/components/AssetTable/columns/Issuer";
import { JURISDICTION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/JurisdictionCountry";
import { MANAGEMENT_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/ManagementFee";
import { MANAGER_COLUMN } from "~/modules/assets/components/AssetTable/columns/Manager";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { PEGGED_ASSET_COLUMN } from "~/modules/assets/components/AssetTable/columns/PeggedAsset";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { REDEMPTION_DESCRIPTION_INLINE } from "~/modules/assets/components/AssetTable/columns/RedemptionDescription";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { SECTORS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Sectors";
import { TICKER_COLUMN } from "~/modules/assets/components/AssetTable/columns/Ticker";
import { TRADITIONAL_CUSTODIAN_WITH_LEI_OR_CIK } from "~/modules/assets/components/AssetTable/columns/TraditionalCustodian";
import { APY_7_DAY_COLUMN } from "~/modules/assets/components/AssetTable/columns/Apy7Day";
import { APY_30_DAY_COLUMN } from "~/modules/assets/components/AssetTable/columns/Apy30Day";
import {
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Performance";
import { TRANSFERABILITY_TYPES_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferabilityTypes";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { TOKENIZATION_SPONSORSHIP_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationSponsorshipType";
import { TOKEN_STANDARD_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenStandard";
import { TOTAL_PERFORMANCE_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TotalPerformanceFee";
import { DESCRIPTION_COLUMN } from "~/modules/assets/components/AssetTable/columns/Description";
import { IS_OPEN_ENDED_COLUMN } from "~/modules/assets/components/AssetTable/columns/IsOpenEnded";
import {
    HOLDER_HAS_RIGHT_TO_REDEEM_COLUMN,
    HOLDER_RIGHTS_DESCRIPTION_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/HolderRightsDescription";
import { USE_OF_INCOME_COLUMN } from "~/modules/assets/components/AssetTable/columns/UseOfIncome";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import { BRIDGED_TOKEN_MARKET_CAP_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenMarketCapDollar";
import { CIRCULATING_ASSET_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/CirculatingAssetValueDollar";
import { CIRCULATING_MARKET_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/CirculatingMarketValueDollar";
import {
    BRIDGED_TOKEN_SUPPLY_TOKEN_COLUMN,
    CIRCULATING_SUPPLY_TOKEN_COLUMN,
    TOTAL_SUPPLY_TOKEN_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/TokenSupply";
import { MARKET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/MarketValue";
import {
    MONTHLY_TRANSFER_COUNT_COLUMN,
    TRAILING_30_DAY_TRANSFER_COUNT_COLUMN,
    TRAILING_7_DAY_TRANSFER_COUNT_COLUMN,
    WEEKLY_TRANSFER_COUNT_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/TransferCount";
import {
    DAILY_TRANSFER_VOLUME_DOLLAR_COLUMN,
    DAILY_TRANSFER_VOLUME_TOKEN_COLUMN,
    MONTHLY_TRANSFER_VOLUME_COLUMN,
    TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN,
    TRAILING_7_DAY_TRANSFER_VOLUME_COLUMN,
    WEEKLY_TRANSFER_VOLUME_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/TransferVolume";
import {
    ANNUALLY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    DAILY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    MONTHLY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    QUARTERLY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    TRAILING_7_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    WEEKLY_ACTIVE_ADDRESSES_COUNT_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/ActiveAddresses";
import {
    TOP_10_HOLDER_CONCENTRATION_COLUMN,
    TOP_5_HOLDER_CONCENTRATION_COLUMN,
    TOP_HOLDER_CONCENTRATION_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/HolderConcentration";
import { TOTAL_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TotalAssetValue";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { HYPOTHETICAL_10K_PERFORMANCE_COLUMN } from "~/modules/assets/components/AssetTable/columns/Hypothetical10KPerformance";
import { CUMULATIVE_ISSUED_AMOUNT_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/CumulativeIssuedAmount";
import { CUMULATIVE_ISSUED_AMOUNT_TOKEN_COLUMN } from "~/modules/assets/components/AssetTable/columns/CumulativeIssuedAmount";
import { NUMBER_OF_DAILY_TRANSACTIONS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransactionCount";
import {
    DAILY_YIELD_DISTRIBUTED_DOLLAR_COLUMN,
    DAILY_YIELD_RATE_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/DailyYield";
import {
    ADJUSTED_DAILY_BURNS_DOLLAR_COLUMN,
    ADJUSTED_DAILY_BURNS_TOKEN_COLUMN,
    ADJUSTED_DAILY_MINTS_DOLLAR_COLUMN,
    ADJUSTED_DAILY_MINTS_TOKEN_COLUMN,
    DAILY_BURNS_DOLLAR_COLUMN,
    DAILY_BURNS_TOKEN_COLUMN,
    DAILY_MINTS_DOLLAR_COLUMN,
    DAILY_MINTS_TOKEN_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/MintsAndBurns";
import { GOVERNING_BODY_COLUMN } from "~/modules/assets/components/AssetTable/columns/GoverningBody";
import { ID_COLUMN } from "~/modules/assets/components/AssetTable/columns/ID";
import { TRANSFERABILITY_DESCRIPTION_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferabilityDescription";
import {
    TERM_DESCRIPTION_COLUMN,
    TERM_LENGTH_YEARS_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Term";
import {
    LOCKUP_DESCRIPTION_COLUMN,
    LOCKUP_PERIOD_MONTHS_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Lockup";
import { FEE_STRUCTURE_DESCRIPTION_COLUMN } from "~/modules/assets/components/AssetTable/columns/FeeStructureDescription";
import { OTHER_FEES_DESCRIPTION_COLUMN } from "~/modules/assets/components/AssetTable/columns/OtherFeesDescription";
import { PEG_MAINTENANCE_DESCRIPTION_COLUMN } from "~/modules/assets/components/AssetTable/columns/PegMaintenanceDescription";
import { TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/Type";

// // // //

export const MAP_REFERENCE_DATA_SLUG_TO_ASSET_TABLE_COLUMN: Record<
    CatalogReferenceDataSlugs,
    Array<ColumnMetadata<AssetTableRowData, AssetColumns>>
> = {
    address: [
        {
            ...CONTRACT_ADDRESS_COLUMN,
            label: "Address",
            tooltip: CatalogReferenceDataSlugs.address,
        },
    ],
    type: [TYPE_COLUMN],
    annual_return_target_description: [ANNUAL_RETURN_TARGET_DESCRIPTION_COLUMN],
    asset_class: [ASSET_CLASS_COLUMN],
    asset_to_pegged_asset_ratio: [ASSET_TO_PEGGED_ASSET_RATIO_COLUMN],
    auditor: [AUDITOR_COLUMN],
    primary_market__base_asset_ticker: [
        { ...PRIMARY_MARKET_BASE_ASSETS_COLUMN, label: "Base Asset Ticker" },
    ],
    crypto_broker: [CRYPTO_BROKER_COLUMN],
    crypto_custodian: [CRYPTO_CUSTODIAN_COLUMN],
    cusip: [CUSIP_COLUMN],
    decimals: [],
    description: [DESCRIPTION_COLUMN],
    dispute_resolution_country: [DISPUTE_RESOLUTION_COUNTRY_COLUMN],
    dispute_resolution_description: [DISPUTE_RESOLUTION_DESCRIPTION_COLUMN],
    distributes_income: [
        { ...USE_OF_INCOME_COLUMN, label: "Distributes Income" },
    ],
    distribution_frequency_type: [DISTRIBUTION_FREQUENCY_TYPE_COLUMN],
    domicile: [INCORPORATION_COUNTRY_COLUMN],
    primary_market__investor_types: [PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN],
    exchange_agent: [EXCHANGE_AGENT_COLUMN],
    fee_structure_description: [FEE_STRUCTURE_DESCRIPTION_COLUMN],
    fund_administrator: [FUND_ADMINISTRATOR_COLUMN],
    governing_body: [GOVERNING_BODY_COLUMN],
    holder_has_right_to_redeem: [HOLDER_HAS_RIGHT_TO_REDEEM_COLUMN],
    holder_rights_description: [HOLDER_RIGHTS_DESCRIPTION_COLUMN],
    icon_url: [],
    id: [ID_COLUMN],
    inception_date: [INCEPTION_DATE_COLUMN],
    isin: [ISIN_COLUMN],
    primary_market__is_kyc_required: [PRIMARY_MARKET_IS_KYC_REQUIRED_COLUMN],
    is_open_ended: [IS_OPEN_ENDED_COLUMN],
    issuer: [ISSUER_COLUMN],
    jurisdiction_country: [JURISDICTION_COUNTRY_COLUMN],
    primary_market__kyc_provider_id: [],
    lockup_description: [LOCKUP_DESCRIPTION_COLUMN],
    lockup_period_months: [LOCKUP_PERIOD_MONTHS_COLUMN],
    total_management_fee_bps: [MANAGEMENT_FEE_COLUMN],
    manager: [MANAGER_COLUMN],
    primary_market__minimum_subscription_amt: [
        PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    ],
    name: [],
    network: [NETWORKS_COLUMN],
    other_fees_description: [
        { ...OTHER_FEES_DESCRIPTION_COLUMN, label: "Other Fees Description" },
    ],
    paying_agent: [PAYING_AGENT_COLUMN],
    pegged_asset: [PEGGED_ASSET_COLUMN],
    peg_maintenance_description: [PEG_MAINTENANCE_DESCRIPTION_COLUMN],
    total_performance_fee_bps: [TOTAL_PERFORMANCE_FEE_COLUMN],
    platform: [PLATFORM_COLUMN],
    primary_market: [],
    primary_market__redemption_description: [
        {
            ...REDEMPTION_DESCRIPTION_INLINE,
            type: "number",
            label: "Redemption Description",
        },
    ],
    primary_market__redemption_fee_bps: [PRIMARY_MARKET_REDEMPTION_FEE_COLUMN],
    primary_market__redemption_frequency: [
        PRIMARY_MARKET_REDEMPTION_TIME_COLUMN,
    ],
    region_ids: [],
    regulatory_framework: [LICENSE_OR_REGULATORY_FRAMEWORK],
    sector_ids: [SECTORS_COLUMN],
    standards: [TOKEN_STANDARD_COLUMN],
    sub_advisor: [SUB_ADVISOR_COLUMN],
    primary_market__subscription_description: [
        PRIMARY_MARKET_SUBSCRIPTION_DESCRIPTION_COLUMN,
    ],
    primary_market__subscription_fee_bps: [
        PRIMARY_MARKET_SUBSCRIPTION_FEE_COLUMN,
    ],
    primary_market__subscription_frequency: [
        PRIMARY_MARKET_SUBSCRIPTION_TIME_COLUMN,
    ],
    term_description: [TERM_DESCRIPTION_COLUMN],
    term_length_years: [TERM_LENGTH_YEARS_COLUMN],
    ticker: [TICKER_COLUMN],
    tokenization_type: [TOKENIZATION_TYPE_COLUMN],
    traditional_broker: [TRADITIONAL_BROKER_COLUMN],
    traditional_custodian: [TRADITIONAL_CUSTODIAN_WITH_LEI_OR_CIK],
    transferability_description: [TRANSFERABILITY_DESCRIPTION_COLUMN],
    transferability_type: [TRANSFERABILITY_TYPES_COLUMN],
    tokenization_sponsorship_type: [TOKENIZATION_SPONSORSHIP_TYPE_COLUMN],
    transfer_agent: [TRANSFER_AGENT_COLUMN],
    website: [WEBSITE_COLUMN],
};

export const MAP_METRIC_SLUG_TO_ASSET_TABLE_COLUMN: Record<
    CatalogMetricSlugs,
    Array<ColumnMetadata<AssetTableRowData, AssetColumns>>
> = {
    adjusted_daily_burns_dollar: [ADJUSTED_DAILY_BURNS_DOLLAR_COLUMN],
    adjusted_daily_burns_token: [ADJUSTED_DAILY_BURNS_TOKEN_COLUMN],
    adjusted_daily_mints_dollar: [ADJUSTED_DAILY_MINTS_DOLLAR_COLUMN],
    adjusted_daily_mints_token: [ADJUSTED_DAILY_MINTS_TOKEN_COLUMN],
    annually_active_addresses_count: [ANNUALLY_ACTIVE_ADDRESSES_COUNT_COLUMN],
    apy_30_day: [APY_30_DAY_COLUMN],
    apy_7_day: [APY_7_DAY_COLUMN],
    bridged_token_market_cap_dollar: [
        {
            ...BRIDGED_TOKEN_MARKET_CAP_DOLLAR_COLUMN,
            label: "Bridged Token Market Cap",
        },
    ],
    bridged_token_supply_token: [
        {
            ...BRIDGED_TOKEN_SUPPLY_TOKEN_COLUMN,
            label: "Bridged Token Supply",
        },
    ],
    bridged_token_value_dollar: [
        {
            ...BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
            label: "Bridged Token Value",
        },
    ],
    circulating_asset_value_dollar: [
        {
            ...CIRCULATING_ASSET_VALUE_DOLLAR_COLUMN,
            label: "Circulating Asset Value",
        },
    ],
    circulating_market_value_dollar: [
        {
            ...CIRCULATING_MARKET_VALUE_DOLLAR_COLUMN,
            label: "Circulating Market Value",
        },
    ],
    circulating_supply_token: [
        {
            ...CIRCULATING_SUPPLY_TOKEN_COLUMN,
            label: "Circulating Supply",
        },
    ],
    cumulative_issued_amount_dollar: [CUMULATIVE_ISSUED_AMOUNT_DOLLAR_COLUMN],
    cumulative_issued_amount_token: [CUMULATIVE_ISSUED_AMOUNT_TOKEN_COLUMN],
    daily_active_addresses_count: [DAILY_ACTIVE_ADDRESSES_COUNT_COLUMN],
    daily_burns_dollar: [DAILY_BURNS_DOLLAR_COLUMN],
    daily_burns_token: [DAILY_BURNS_TOKEN_COLUMN],
    daily_mints_dollar: [DAILY_MINTS_DOLLAR_COLUMN],
    daily_mints_token: [DAILY_MINTS_TOKEN_COLUMN],
    daily_transfer_volume_dollar: [DAILY_TRANSFER_VOLUME_DOLLAR_COLUMN],
    daily_transfer_volume_token: [DAILY_TRANSFER_VOLUME_TOKEN_COLUMN],
    daily_yield_distributed_dollar: [DAILY_YIELD_DISTRIBUTED_DOLLAR_COLUMN],
    daily_yield_rate: [DAILY_YIELD_RATE_COLUMN],
    holding_addresses_count: [
        {
            ...HOLDING_ADDRESS_COUNT_COLUMN,
            label: "Holding Addresses",
        },
    ],
    hypothetical_10_000_performance: [HYPOTHETICAL_10K_PERFORMANCE_COLUMN],
    market_value_dollar: [{ ...MARKET_VALUE_COLUMN, label: "Market Value" }],
    monthly_active_addresses_count: [MONTHLY_ACTIVE_ADDRESSES_COUNT_COLUMN],
    monthly_transfer_count: [MONTHLY_TRANSFER_COUNT_COLUMN],
    monthly_transfer_volume: [MONTHLY_TRANSFER_VOLUME_COLUMN],
    net_asset_value_dollar: [
        {
            ...NET_ASSET_VALUE_COLUMN,
            label: "Net Asset Value",
        },
    ],
    number_of_daily_transactions_count: [
        NUMBER_OF_DAILY_TRANSACTIONS_COUNT_COLUMN,
    ],
    price_dollar: [PRICE_COLUMN],
    quarterly_active_addresses_count: [QUARTERLY_ACTIVE_ADDRESSES_COUNT_COLUMN],
    top_10_holder_concentration: [TOP_10_HOLDER_CONCENTRATION_COLUMN],
    top_5_holder_concentration: [TOP_5_HOLDER_CONCENTRATION_COLUMN],
    top_holder_concentration: [TOP_HOLDER_CONCENTRATION_COLUMN],
    total_asset_value_dollar: [
        { ...TOTAL_ASSET_VALUE_COLUMN, label: "Total Asset Value" },
    ],
    total_supply_token: [
        {
            ...TOTAL_SUPPLY_TOKEN_COLUMN,
            label: "Total Supply",
        },
    ],
    trailing_1_month_return_percent: [PERFORMANCE_1_MONTH_COLUMN],
    trailing_1_year_return_percent: [PERFORMANCE_1_YEAR_COLUMN],
    trailing_30_day_active_addresses_count: [
        {
            ...TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
            label: "Trailing 30 Day Active Addresses",
        },
    ],
    trailing_30_day_transfer_count: [
        {
            ...TRAILING_30_DAY_TRANSFER_COUNT_COLUMN,
            label: "Trailing 30 Day Transfer Count",
        },
    ],
    trailing_30_day_transfer_volume: [
        {
            ...TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN,
            label: "Trailing 30 Day Transfer Volume",
        },
    ],
    trailing_7_day_active_addresses_count: [
        {
            ...TRAILING_7_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
            label: "Trailing 7 Day Active Addresses",
        },
    ],
    trailing_7_day_transfer_count: [
        {
            ...TRAILING_7_DAY_TRANSFER_COUNT_COLUMN,
            label: "Trailing 7 Day Transfer Count",
        },
    ],
    trailing_7_day_transfer_volume: [
        {
            ...TRAILING_7_DAY_TRANSFER_VOLUME_COLUMN,
            label: "Trailing 7 Day Transfer Volume",
        },
    ],
    weekly_active_addresses_count: [WEEKLY_ACTIVE_ADDRESSES_COUNT_COLUMN],
    weekly_transfer_count: [WEEKLY_TRANSFER_COUNT_COLUMN],
    weekly_transfer_volume: [WEEKLY_TRANSFER_VOLUME_COLUMN],
};

// // // //

export function getAssetTableColumnByCatalogSlug(
    slug: CatalogReferenceDataSlugs | CatalogMetricSlugs,
): Array<ColumnMetadata<AssetTableRowData, AssetColumns>> {
    const referenceDataColumns =
        MAP_REFERENCE_DATA_SLUG_TO_ASSET_TABLE_COLUMN[
            slug as CatalogReferenceDataSlugs
        ] ?? [];

    const metricColumns =
        MAP_METRIC_SLUG_TO_ASSET_TABLE_COLUMN[slug as CatalogMetricSlugs] ?? [];

    // Set proPlanRequired to true for all reference data + metric columns
    return [
        ...referenceDataColumns.map((c) => {
            return {
                ...c,
                loginRequired: false,
                proPlanRequired: true,
            };
        }),
        ...metricColumns.map((c) => {
            return {
                ...c,
                loginRequired: false,
                proPlanRequired: true,
            };
        }),
    ];
}
